/** Static SVG helpers shared by every piece kit. Legacy serialization stays byte-identical. */
import type { PaletteRole, PieceRecipe, ResolvedPalette } from './contract';
import type { Point } from './contract';
import { PIECE_PALETTES } from './palettes';
export type { PaletteRole, PieceRecipe, PieceArt, HideoutPieceArt, Point, ResolvedPalette } from './contract';
export { rngFor } from './rng';

export const n = (v: number): string => {
  if (!Number.isFinite(v)) throw new Error('hush hideout: nonfinite geometry');
  return String(Math.round(v * 1000) / 1000);
};

export type Attrs = Record<string, string | number | undefined>;
export function el(tag: string, attrs: Attrs, inner = ''): string {
  const a = Object.entries(attrs).filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}="${typeof v === 'number' ? n(v) : v}"`).join(' ');
  return inner ? `<${tag} ${a}>${inner}</${tag}>` : `<${tag} ${a}/>`;
}

/** Linear mix of two #rrggbb colors. */
export function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ch = (s: number) => Math.round(((pa >> s) & 255) + (((pb >> s) & 255) - ((pa >> s) & 255)) * t);
  return '#' + ((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0');
}

/** Polygon/polyline path from points. */
export function poly(points: readonly Point[], close = true): string {
  return points.map((p, i) => `${i ? 'L' : 'M'}${n(p.x)} ${n(p.y)}`).join(' ') + (close ? 'Z' : '');
}

export function checkScope(scope: string): void {
  if (!/^[a-z0-9_-]{1,80}$/i.test(scope)) throw new Error('hush hideout: unsafe definition scope');
}

export function checkEnvelope(r: unknown, generatorId: string,
  palettes: Readonly<Record<string, ResolvedPalette>> = PIECE_PALETTES): void {
  const x = r as PieceRecipe<string, unknown>;
  if (!x || typeof x !== 'object' || x.schema !== 'hush.asset-recipe.v1' || x.generatorId !== generatorId) {
    throw new Error(`hush hideout recipe: expected ${generatorId}`);
  }
  if (typeof x.id !== 'string' || !Number.isInteger(x.seed)) throw new Error(`${generatorId}: id/seed`);
  if (typeof x.paletteId !== 'string' || !Object.prototype.hasOwnProperty.call(palettes, x.paletteId)) throw new Error(`${generatorId}: paletteId`);
  if (!x.params || typeof x.params !== 'object') throw new Error(`${generatorId}: params`);
}

/** Group with a data-part marker, recording the part name. */
export function partGroup(parts: string[], part: string, inner: string, extra: Attrs = {}): string {
  parts.push(part);
  return el('g', { 'data-part': part, ...extra }, inner);
}

export function role(roles: ResolvedPalette, r: PaletteRole): string { return roles[r]; }
